import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import { blue, green, orange, purple, red, teal } from '@mui/material/colors';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
// eslint-disable-next-line no-restricted-imports -- pre-existing direct DataGridPro use; migrate to ServerDataGrid when touched
import { DataGridPro } from '@mui/x-data-grid-pro';
import moment from 'moment';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import DataGridToolbar from '@sophocles/data-grids/components/toolbar';

const SOURCE_COLORS = {
  location: green[500],
  radio_history: blue[500],
  prepaid_ledger: purple[500],
  order: orange[700],
  rma: red[400],
  billing: teal[500],
};

function getSourceLabels(t) {
  return {
    location: t('radio.timeline.sourceLocation'),
    radio_history: t('radio.timeline.sourceRadioHistory'),
    prepaid_ledger: t('radio.timeline.sourcePrepaidLedger'),
    order: t('radio.timeline.sourceOrder'),
    rma: t('radio.timeline.sourceRma'),
    billing: t('radio.timeline.sourceBilling'),
  };
}

const ALL_SOURCES = ['location', 'radio_history', 'prepaid_ledger', 'order', 'rma', 'billing'];
const EXPANDABLE_SOURCES = new Set(['order', 'rma', 'billing']);

function getColumns(t, sourceLabels) {
  return [
    {
      field: 'timestamp',
      headerName: t('radio.timeline.columnTimestamp'),
      width: 110,
      renderCell: params => (
        <span title={moment(params.value).format('L - LTS')} style={{ whiteSpace: 'nowrap' }}>
          {moment(params.value).fromNow()}
        </span>
      ),
    },
    {
      field: 'event_type',
      headerName: t('radio.timeline.columnEvent'),
      width: 155,
      renderCell: params => (
        <Chip
          label={params.value ? params.value.replace(/_/g, ' ') : t('general.unknown')}
          size="small"
          sx={{ textTransform: 'capitalize', fontSize: '0.75rem' }}
        />
      ),
    },
    {
      field: 'source',
      headerName: t('radio.timeline.columnSource'),
      width: 105,
      renderCell: params => {
        const color = SOURCE_COLORS[params.value] || grey[500];
        const label = sourceLabels[params.value] || params.value || t('general.unknown');
        return (
          <Chip
            label={label}
            size="small"
            sx={{
              backgroundColor: color,
              color: 'white',
              fontSize: '0.75rem',
              height: 22,
            }}
          />
        );
      },
    },
    {
      field: 'summary',
      headerName: t('radio.timeline.columnSummary'),
      flex: 1,
      minWidth: 150,
      sortable: false,
    },
    {
      field: 'actor',
      headerName: t('radio.timeline.columnUser'),
      flex: 0.4,
      minWidth: 100,
      sortable: false,
      valueGetter: value => value?.name || null,
      renderCell: params =>
        params.value ? (
          <span>{params.value}</span>
        ) : (
          <span style={{ color: 'var(--text-secondary)' }}>{t('radio.timeline.systemActor')}</span>
        ),
    },
  ];
}

// ── Detail panel for expandable rows ────────────────────────────────────

function DetailField({ label, value, linkTo }) {
  if (value == null || value === '' || value === 'N/A') return null;
  return (
    <Typography variant="body2" sx={{ mr: 3 }}>
      <strong>{label}:</strong>{' '}
      {linkTo ? (
        <Link to={linkTo} style={{ color: blue[700] }}>
          {value}
        </Link>
      ) : (
        value
      )}
    </Typography>
  );
}

function OrderDetailPanel({ details }) {
  const { t } = useTranslation();

  const billingRange =
    details.billing_type === 'Prepaid' && details.billing_start_at && details.term_length_prepaid
      ? `${moment(details.billing_start_at).format('L')} – ${moment(details.billing_start_at)
          .add(details.term_length_prepaid, 'months')
          .format('L')}`
      : null;

  return (
    <Box sx={{ p: 2, pl: 6, backgroundColor: 'var(--surface-secondary)' }}>
      <Stack direction="row" flexWrap="wrap" spacing={0} sx={{ gap: '4px 0' }}>
        <DetailField
          label={t('radio.timeline.detailOrder')}
          value={`#${details.order_id}`}
          linkTo={`/sales/orders/${details.order_id}`}
        />
        <DetailField label={t('radio.timeline.detailStatus')} value={details.status} />
        <DetailField label={t('radio.timeline.detailCustomer')} value={details.customer} />
        <DetailField
          label={t('radio.timeline.detailSiteGroup')}
          value={
            details.site_group_description
              ? `${details.site_group} - ${details.site_group_description}`
              : details.site_group
          }
        />
        <DetailField label={t('radio.timeline.detailBillingType')} value={details.billing_type} />
        {billingRange && <DetailField label={t('radio.timeline.detailPrepaidTerm')} value={billingRange} />}
        <DetailField
          label={t('radio.timeline.detailBillingStart')}
          value={details.billing_start_at ? moment(details.billing_start_at).format('L') : null}
        />
        <DetailField
          label={t('radio.timeline.detailInvoiceDate')}
          value={details.invoice_date ? moment(details.invoice_date).format('L') : null}
        />
        <DetailField
          label={t('radio.timeline.detailShipDate')}
          value={details.ship_date ? moment(details.ship_date).format('L') : null}
        />
        <DetailField label={t('radio.timeline.detailTracking')} value={details.tracking} />
      </Stack>
    </Box>
  );
}

function RmaDetailPanel({ details }) {
  const { t } = useTranslation();

  return (
    <Box sx={{ p: 2, pl: 6, backgroundColor: 'var(--surface-secondary)' }}>
      <Stack direction="row" flexWrap="wrap" spacing={0} sx={{ gap: '4px 0' }}>
        <DetailField
          label={t('radio.timeline.detailRma')}
          value={
            details.is_quick_ship ? (
              <span>
                #{details.rma_number}{' '}
                <i title={t('radio.timeline.quickShip')} className="fas fa-bolt" style={{ color: green[500] }} />
              </span>
            ) : (
              `#${details.rma_number}`
            )
          }
          linkTo={`/rma/${details.rma_id}/view`}
        />
        <DetailField label={t('radio.timeline.detailStatus')} value={details.status} />
        <DetailField label={t('radio.timeline.detailCustomer')} value={details.customer} />
        <DetailField
          label={t('radio.timeline.detailSiteGroup')}
          value={
            details.site_group_description
              ? `${details.site_group} - ${details.site_group_description}`
              : details.site_group
          }
        />
        <DetailField label={t('radio.timeline.detailOldRadio')} value={details.old_radio} />
        <DetailField label={t('radio.timeline.detailReplacementRadio')} value={details.replacement_radio} />
      </Stack>
    </Box>
  );
}

function BillingDetailPanel({ details }) {
  const { t } = useTranslation();

  return (
    <Box sx={{ p: 2, pl: 6, backgroundColor: 'var(--surface-secondary)' }}>
      <Stack direction="row" flexWrap="wrap" spacing={0} sx={{ gap: '4px 0' }}>
        <DetailField
          label={t('radio.timeline.detailInvoice')}
          value={`#${details.invoice_number}`}
          linkTo={`/billing/invoices/${details.invoice_number}/`}
        />
        <DetailField
          label={t('radio.timeline.detailInvoiceDate')}
          value={details.invoice_date ? moment(details.invoice_date).format('L') : null}
        />
        <DetailField label={t('radio.timeline.detailCustomer')} value={details.customer} />
        <DetailField label={t('radio.timeline.detailSiteGroup')} value={details.site_group} />
        <DetailField label={t('radio.timeline.detailDescription')} value={details.description} />
        <DetailField
          label={t('radio.timeline.detailPrice')}
          value={details.price ? `$${Number(details.price).toFixed(2)}` : null}
        />
      </Stack>
    </Box>
  );
}

function DetailPanelContent({ row }) {
  const { source, details } = row;
  if (!details) return null;

  switch (source) {
    case 'order':
      return <OrderDetailPanel details={details} />;
    case 'rma':
      return <RmaDetailPanel details={details} />;
    case 'billing':
      return <BillingDetailPanel details={details} />;
    default:
      return null;
  }
}

function SourceFilterChips({ activeSources, onToggle }) {
  const { t } = useTranslation();
  const sourceLabels = useMemo(() => getSourceLabels(t), [t]);

  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      {ALL_SOURCES.map(source => {
        const isActive = activeSources.includes(source);
        return (
          <Chip
            key={source}
            label={sourceLabels[source]}
            size="small"
            variant={isActive ? 'filled' : 'outlined'}
            onClick={() => onToggle(source)}
            sx={{
              backgroundColor: isActive ? SOURCE_COLORS[source] : 'transparent',
              color: isActive ? 'white' : SOURCE_COLORS[source],
              borderColor: SOURCE_COLORS[source],
              '&:hover': {
                backgroundColor: isActive ? SOURCE_COLORS[source] : `${SOURCE_COLORS[source]}20`,
              },
            }}
          />
        );
      })}
    </Stack>
  );
}

// ── Main component ──────────────────────────────────────────────────────

function UnifiedTimelineTable({ data }) {
  const { t } = useTranslation();
  const [activeSources, setActiveSources] = useState(ALL_SOURCES);

  const sourceLabels = useMemo(() => getSourceLabels(t), [t]);

  const columns = useMemo(() => getColumns(t, sourceLabels), [t, sourceLabels]);

  const toggleSource = source => {
    setActiveSources(prev => (prev.includes(source) ? prev.filter(s => s !== source) : [...prev, source]));
  };

  const rows = useMemo(() => {
    if (!data) return [];
    const plain = data.toJS ? data.toJS() : data;
    const activeSet = new Set(activeSources);
    return plain.filter(row => activeSet.has(row.source)).map((row, idx) => ({ _rowId: idx, ...row }));
  }, [data, activeSources]);

  const getDetailPanelContent = useCallback(
    ({ row }) => (EXPANDABLE_SOURCES.has(row.source) ? <DetailPanelContent row={row} /> : null),
    [],
  );

  const getDetailPanelHeight = useCallback(() => 'auto', []);

  return (
    <Paper style={{ marginBottom: '16px', padding: 16 }}>
      <h4 style={{ margin: '0 0 8px' }}>{t('radio.timeline.title')}</h4>
      <DataGridToolbar
        showQuickFilter={false}
        toolbarContent={<SourceFilterChips activeSources={activeSources} onToggle={toggleSource} />}
      />
      <DataGridPro
        rows={rows}
        columns={columns}
        getRowId={row => row._rowId}
        autoHeight
        pagination
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
          sorting: { sortModel: [{ field: 'timestamp', sort: 'desc' }] },
        }}
        disableRowSelectionOnClick
        disableColumnMenu
        slots={{
          detailPanelExpandIcon: ChevronRightIcon,
          detailPanelCollapseIcon: ExpandMoreIcon,
        }}
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={getDetailPanelHeight}
        sx={{ '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600 } }}
      />
    </Paper>
  );
}

export default UnifiedTimelineTable;
